/**
 * The company filter on the Property History page selects the company that was
 * responsible for a service (`filter.servicedBy`), which may be a partner
 * company the viewer does not administer. `filter.companies` is a separate
 * thing: it is the access scope the backend checks the viewer against, and it
 * only ever accepts companies the viewer administers. That means the scope has
 * to be derived from the selection rather than driven by it directly.
 */
export default function deriveCompanyScope({
  servicedBy,
  adminCompanyIds,
  currentCompanyId,
}: {
  servicedBy: string[];
  adminCompanyIds: string[];
  currentCompanyId?: string | null;
}): string[] {
  if (servicedBy.length === 0) {
    // Nothing selected, so keep the page scoped to the company the viewer is
    // currently in, the same as it was before the servicedBy filter existed.
    return currentCompanyId ? [currentCompanyId] : [];
  }

  const adminIds = new Set(adminCompanyIds);
  const selectedOwnCompanies = servicedBy.filter((id) => adminIds.has(id));
  if (selectedOwnCompanies.length === servicedBy.length) {
    // Every selection is a company the viewer administers, so the records those
    // companies performed all sit inside those same companies.
    return selectedOwnCompanies;
  }

  // At least one partner company is selected. A partner only matches records
  // where it sits directly below one of the viewer's companies, and we cannot
  // tell from the dropdown which of those companies shared the work to it, so
  // the scope has to span all of them. An empty list is how the backend is told
  // to use every company the viewer administers.
  return [];
}
